"use client";

import { Button } from "@/components/ui/button";
import { AnimatedLink } from "@/components/ui/animated-link";
import Image from "next/image";
import Link from "next/link";
import { StarIcon } from "@heroicons/react/24/solid";
import LearnMoreSection from "@/components/molecules/landing-section-two/learn-more";
import FeaturesSection from "@/components/molecules/landing-section-two/shop-with";
import CTASection from "@/components/molecules/landing-section-two/discover";
import Footer from "@/components/molecules/landing-section-two/footer";
import NavBar from "@/components/molecules/nav-bar";
import PaymentOptionsCards from "@/components/molecules/landing-section-two/payment-options-cards";
import FaqComponent from "@/components/molecules/faq-component";
import JoinEcosystem from "@/components/molecules/landing-section-two/join-ecosystem";
import { getMarketplaceUrl } from "@/lib/marketplace-utils";

export default function Home() {
  const reviews = [
    {
      title: "Reliable and Stress-Free",
      content:
        "“Shopping on credit has never been this simple. The verification process was quick, my order was delivered on time, and repayment was automatically handled with no stress. It feels like the platform was designed with people like me in mind.”",
      name: "Chika",
      location: "Abuja",
    },
    {
      title: "A True Lifesaver",
      content:
        "“Being able to buy essentials and pay later with my salary came through when I needed it the most. I didn’t have to borrow from friends or cut corners. Onyx gave me peace of mind, and I’ll definitely keep using it.”",
      name: "Ibrahim",
      location: "Kano",
    },
    {
      title: "Secure and Transparent",
      content:
        "“I was a bit skeptical at first, but the process was very straightforward. No hidden charges, no surprises — everything was clear from start to finish. It feels good to finally use a system I can actually trust.”",
      name: "Funke",
      location: "Lagos",
    },
    {
      title: "Exactly What I Needed",
      content:
        "“From appliances to everyday items, I got everything I wanted without worrying about immediate payment. Repayment was effortless, and I appreciated how smooth the whole experience was. It’s perfect for balancing work, life, and family needs.”",
      name: "Tunde",
      location: "Port Harcourt",
    },
  ];
  return (
    <div>
      <NavBar />
      <div className='mx-auto h-full max-w-screen-2xl overflow-x-hidden scroll-smooth'>
        <section className='flex w-full flex-col md:flex-row md:justify-between md:gap-15 md:pl-10 2xl:pl-10 2xl:md:gap-[200px]'>
          <div className='mx-auto mt-28 flex w-fit max-w-lg flex-col items-center gap-4 px-4 md:mx-0 md:mt-[230px] md:max-w-2xl md:items-start md:gap-5 md:px-0'>
            <h1 className='text-text-title text-center text-2xl leading-[110%] font-semibold tracking-[0%] sm:text-3xl md:text-left md:text-5xl md:leading-[120%] lg:text-[53px] 2xl:text-7xl'>
              Onyx: Shop Online Easily with Flexible Payment Plans.
            </h1>
            <div className='mx-auto flex w-fit'>
              <p className='text-text-title text-center text-sm leading-[115%] tracking-[0%] md:text-left md:font-medium lg:text-base 2xl:text-lg'>
                Shop phones, laptops, electronics, and everyday essentials. Pay now, pay later, or spread your payments
                with Onyx<span className='text-link-primary'>.</span>
              </p>
            </div>

            <AnimatedLink
              href={getMarketplaceUrl("/shop")}
              showHoverCircle={false}
              className='bg-btn-primary text-text-title-inv dark:text-text-title inline-flex h-12 w-[130px] cursor-pointer gap-0 rounded-full px-4 py-2 text-xs font-semibold whitespace-nowrap shadow-xs select-none md:h-9 md:w-[155px] md:text-base lg:w-[175px] lg:py-[24px] 2xl:w-[180px] 2xl:py-6 2xl:text-base'
            >
              <span>
                Shop with Onyx<span className='text-link-primary'>.</span>
              </span>
            </AnimatedLink>
          </div>
          <div className='mt-5 md:-mt-14 md:-mr-80'>
            <Image
              src='/assets/images/hero-img.png'
              alt='Hero Image'
              width={870}
              priority
              height={860}
              className='mx-auto w-auto rounded-full md:mx-0 md:h-[800px] 2xl:h-[860px]'
            />
          </div>
        </section>
        <section className='bg-bg-base relative w-full px-4 md:-top-15 md:px-10'>
          <div>
            <div className='text-text-title pt-10 pb-5 text-center text-sm font-medium tracking-[0%] md:text-lg 2xl:text-xl'>
              Meet Onyx<span className='text-link-primary'>.</span>
            </div>
            <p className='text-text-title mx-auto my-0 py-0 text-center text-sm tracking-[0%] md:text-2xl lg:max-w-[57.666%] 2xl:text-3xl'>
              A modern online marketplace that helps you shop smarter. We connect trusted vendors, flexible payment
              options, and secure delivery, all in one place. Whether you want to pay upfront, buy now and pay later, or
              combine both, Onyx gives you control over how you shop.
            </p>
          </div>
          <div className='relative mt-10 aspect-1828/808 md:overflow-hidden'>
            <Image
              src='/assets/images/Onyx_image4.png'
              alt='Meet Onyx'
              width={1828}
              height={808}
              className='h-full w-full object-cover object-center'
            />
            <Link href={getMarketplaceUrl("/shop")}>
              <Button className='absolute -bottom-12 left-1/2 flex -translate-x-1/2 cursor-pointer gap-0 rounded-full text-xs font-semibold duration-200 ease-in-out hover:scale-95 md:bottom-10 md:w-[155px] md:bg-[#FAFAFA] md:py-5 md:text-[#0B0B0B] lg:py-[24px] lg:text-[13px] 2xl:w-[180px] 2xl:py-6 2xl:text-base'>
                <span>
                  Shop with Onyx<span className='text-link-primary'>.</span>
                </span>
              </Button>
            </Link>
          </div>
          <div className='mx-auto mt-16 grid w-full grid-cols-1 items-stretch gap-6 px-4 md:mt-12 md:w-full md:grid-cols-4 md:justify-between md:gap-10'>
            {reviews.map((review, index) => (
              <div key={index} className='flex flex-col items-center gap-1.5 md:gap-2.5 2xl:gap-3'>
                <div className='flex items-center gap-1'>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <StarIcon key={index} className='text-link-primary size-4' />
                  ))}
                </div>
                <div className='text-text-title text-sm font-semibold md:text-base 2xl:text-lg'>{review.title}</div>
                <div className='text-text-title text-center text-sm font-medium md:text-base 2xl:text-base'>
                  {review.content}
                </div>
                <div className='text-text-title mt-auto text-sm font-semibold md:text-base 2xl:text-base'>
                  {review.name}, {review.location}
                </div>
              </div>
            ))}
          </div>
          <PaymentOptionsCards />
          <LearnMoreSection />
          <FeaturesSection />
          <h3 id='faq' className='text-text-title text-center text-2xl md:text-3xl'>
            FAQs
          </h3>
          <div className='px-4 md:px-10'>
            <FaqComponent />
          </div>
          <JoinEcosystem />
          <CTASection />
        </section>
        <Footer />
      </div>
    </div>
  );
}
